import { useEffect, useState } from "react";
import { Calendar as CalendarIcon, Download, CheckCircle2, Clock, Mail, Phone, User, Copy, ExternalLink } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  preferred_date: string;
  preferred_time: string;
  message: string | null;
  status: string;
  created_at: string;
}

const AdminCalendar = () => {
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [loading, setLoading] = useState(true);
  const calendarFeedUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-feed`;

  useEffect(() => {
    fetchAppointments();
    
    // Automatische Aktualisierung alle 5 Minuten
    const interval = setInterval(() => {
      fetchAppointments();
    }, 5 * 60 * 1000); // 5 Minuten in Millisekunden
    
    return () => clearInterval(interval);
  }, []);

  const fetchAppointments = async () => {
    try {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .order("preferred_date", { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error: any) {
      console.error("Error fetching appointments:", error);
      toast({
        title: "Fehler beim Laden",
        description: "Die Termine konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getDatesWithAppointments = () => {
    return appointments.map(apt => new Date(apt.preferred_date));
  };

  const getAppointmentsForSelectedDate = () => {
    if (!selectedDate) return [];
    
    return appointments.filter(apt => {
      const aptDate = new Date(apt.preferred_date);
      return (
        aptDate.getDate() === selectedDate.getDate() &&
        aptDate.getMonth() === selectedDate.getMonth() &&
        aptDate.getFullYear() === selectedDate.getFullYear()
      );
    });
  };

  const exportToICS = (appointment: Appointment) => {
    const startDate = new Date(appointment.preferred_date);
    const [hours, minutes] = appointment.preferred_time.split(':').map(Number);
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setMinutes(startDate.getMinutes() + 30);

    const formatICSDate = (date: Date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hour = String(date.getUTCHours()).padStart(2, '0');
      const minute = String(date.getUTCMinutes()).padStart(2, '0');
      const second = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hour}${minute}${second}Z`;
    };

    const teamsLink = "https://teams.microsoft.com/l/meetup-join/19%3ameeting_NWUwZTBhZjMtY2FhNC00NTU1LWExY2YtMThlOTYzZThlMTg0%40thread.v2/0?context=%7b%22Tid%22%3a%224897b68e-7063-4af5-bbf8-a6d86752e545%22%2c%22Oid%22%3a%2202ffc0a8-ede5-4eff-a9b2-5daafa8dda73%22%7d";

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EAP Abutments//Appointment//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `UID:appointment-${appointment.id}@eap-abutments.com`,
      `SUMMARY:Videocall - ${appointment.name}`,
      `DESCRIPTION:Videocall-Termin mit ${appointment.name}\\n\\nE-Mail: ${appointment.email}\\n${appointment.phone ? `Telefon: ${appointment.phone}\\n` : ''}\\nTeams Link: ${teamsLink}${appointment.message ? `\\n\\nNachricht: ${appointment.message}` : ''}`,
      'LOCATION:Microsoft Teams (Online)',
      `URL:${teamsLink}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Erinnerung: Videocall in 15 Minuten',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `termin-${appointment.name.replace(/\s+/g, '-')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Termin exportiert",
      description: "Der Termin wurde als .ics Datei heruntergeladen.",
    });
  };

  const exportAllToICS = () => {
    const teamsLink = "https://teams.microsoft.com/l/meetup-join/19%3ameeting_NWUwZTBhZjMtY2FhNC00NTU1LWExY2YtMThlOTYzZThlMTg0%40thread.v2/0?context=%7b%22Tid%22%3a%224897b68e-7063-4af5-bbf8-a6d86752e545%22%2c%22Oid%22%3a%2202ffc0a8-ede5-4eff-a9b2-5daafa8dda73%22%7d";

    const formatICSDate = (date: Date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hour = String(date.getUTCHours()).padStart(2, '0');
      const minute = String(date.getUTCMinutes()).padStart(2, '0');
      const second = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hour}${minute}${second}Z`;
    };

    const events = appointments.map(apt => {
      const startDate = new Date(apt.preferred_date);
      const [hours, minutes] = apt.preferred_time.split(':').map(Number);
      startDate.setHours(hours, minutes, 0, 0);
      
      const endDate = new Date(startDate);
      endDate.setMinutes(startDate.getMinutes() + 30);

      return [
        'BEGIN:VEVENT',
        `DTSTART:${formatICSDate(startDate)}`,
        `DTEND:${formatICSDate(endDate)}`,
        `DTSTAMP:${formatICSDate(new Date())}`,
        `UID:appointment-${apt.id}@eap-abutments.com`,
        `SUMMARY:Videocall - ${apt.name}`,
        `DESCRIPTION:Videocall-Termin mit ${apt.name}\\n\\nE-Mail: ${apt.email}\\n${apt.phone ? `Telefon: ${apt.phone}\\n` : ''}\\nTeams Link: ${teamsLink}${apt.message ? `\\n\\nNachricht: ${apt.message}` : ''}`,
        'LOCATION:Microsoft Teams (Online)',
        `URL:${teamsLink}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        'DESCRIPTION:Erinnerung: Videocall in 15 Minuten',
        'END:VALARM',
        'END:VEVENT'
      ].join('\r\n');
    }).join('\r\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EAP Abutments//Appointments//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      events,
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'alle-termine.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Alle Termine exportiert",
      description: `${appointments.length} Termine wurden als .ics Datei heruntergeladen.`,
    });
  };

  const selectedDateAppointments = getAppointmentsForSelectedDate();

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <AdminNavigation />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Terminkalender</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Ihre Termine und exportieren Sie sie zu Outlook oder Google Kalender
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Kalenderübersicht
              </CardTitle>
              <CardDescription>
                Wählen Sie ein Datum um Termine anzuzeigen
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={de}
                modifiers={{
                  booked: getDatesWithAppointments(),
                }}
                modifiersStyles={{
                  booked: {
                    fontWeight: 'bold',
                    backgroundColor: 'hsl(var(--primary) / 0.2)',
                  },
                }}
                className="max-w-full rounded-md border p-1 sm:p-3"
                classNames={{
                  head_cell: "w-8 text-[0.7rem] font-normal text-muted-foreground sm:w-9 sm:text-[0.8rem]",
                  cell: "h-8 w-8 p-0 text-center text-xs relative sm:h-9 sm:w-9 sm:text-sm",
                  day: "h-8 w-8 p-0 text-xs font-normal aria-selected:opacity-100 sm:h-9 sm:w-9 sm:text-sm",
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Kalender-Abonnement</CardTitle>
              <CardDescription>
                Synchronisieren Sie automatisch mit Outlook oder Google Kalender
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">📅 iCal Feed URL:</p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={calendarFeedUrl}
                    readOnly
                    className="min-w-0 flex-1 px-3 py-2 text-xs bg-background border rounded font-mono"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(calendarFeedUrl);
                      toast({
                        title: "URL kopiert!",
                        description: "Die Feed-URL wurde in die Zwischenablage kopiert.",
                      });
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="font-semibold mb-2 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    So fügen Sie den Feed in Outlook hinzu:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground ml-1">
                    <li>Öffnen Sie Outlook</li>
                    <li>Gehen Sie zu "Kalender"</li>
                    <li>Klicken Sie auf "Kalender hinzufügen" → "Aus dem Internet"</li>
                    <li>Fügen Sie die obige URL ein</li>
                    <li>Der Kalender synchronisiert sich automatisch</li>
                  </ol>
                </div>

                <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="font-semibold mb-2 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    So fügen Sie den Feed in Google Kalender hinzu:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-xs text-muted-foreground ml-1">
                    <li>Öffnen Sie Google Kalender</li>
                    <li>Klicken Sie auf das "+" neben "Andere Kalender"</li>
                    <li>Wählen Sie "Per URL"</li>
                    <li>Fügen Sie die obige URL ein</li>
                    <li>Klicken Sie auf "Kalender hinzufügen"</li>
                  </ol>
                </div>

                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-xs">
                    <strong>💡 Hinweis:</strong> Der Kalender aktualisiert sich automatisch stündlich. 
                    Neue oder geänderte Termine erscheinen innerhalb von 1 Stunde in Ihrem Kalender.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Export-Optionen</CardTitle>
              <CardDescription>
                Einmalige Exporte für Offline-Nutzung
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold mb-2">Gesamtstatistik</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Gesamt</p>
                    <p className="text-2xl font-bold">{appointments.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Bestätigt</p>
                    <p className="text-2xl font-bold text-green-600">
                      {appointments.filter(a => a.status === 'confirmed').length}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={exportAllToICS}
                className="w-full"
                disabled={appointments.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Alle Termine exportieren (.ics)
              </Button>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>💡 Die .ics Datei kann in folgenden Programmen geöffnet werden:</p>
                <ul className="list-disc list-inside ml-2">
                  <li>Microsoft Outlook</li>
                  <li>Google Calendar (Import)</li>
                  <li>Apple Kalender</li>
                  <li>Thunderbird</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {selectedDate && (
          <Card>
            <CardHeader>
              <CardTitle>
                Termine am {format(selectedDate, "EEEE, dd. MMMM yyyy", { locale: de })}
              </CardTitle>
              <CardDescription>
                {selectedDateAppointments.length} {selectedDateAppointments.length === 1 ? 'Termin' : 'Termine'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedDateAppointments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Termine für diesen Tag</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDateAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold">{apt.preferred_time} Uhr</span>
                          <Badge variant={apt.status === 'confirmed' ? 'default' : 'secondary'}>
                            {apt.status === 'confirmed' ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Bestätigt
                              </>
                            ) : (
                              'Ausstehend'
                            )}
                          </Badge>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportToICS(apt)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Exportieren
                        </Button>
                      </div>

                      <div className="grid gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{apt.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <a
                            href={`mailto:${apt.email}`}
                            className="text-primary hover:underline"
                          >
                            {apt.email}
                          </a>
                        </div>
                        {apt.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <a
                              href={`tel:${apt.phone}`}
                              className="text-primary hover:underline"
                            >
                              {apt.phone}
                            </a>
                          </div>
                        )}
                        {apt.message && (
                          <div className="mt-2 p-3 bg-muted rounded text-sm">
                            <p className="font-medium mb-1">Nachricht:</p>
                            <p className="text-muted-foreground">{apt.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AdminCalendar;
